import { notifyError } from "@/lib/notify-error";
import { useState } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CONVERSION_CHUNK_SIZE = 100;

export interface ConvertTagDetectionLocation {
  detectionId: Id<"documentIntelligenceDetections">;
  alreadyConverted: boolean;
  planDocumentName: string;
}

export interface ConvertTagPointsChunkResult {
  createdItemIds: Id<"fieldPilotItems">[];
  skippedExistingItemIds: Id<"fieldPilotItems">[];
  skippedInactiveDetectionCount: number;
}

export interface ConvertTagPointsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tagLabel: string;
  defaultStatusName: string;
  unconvertedLocations: ConvertTagDetectionLocation[];
  documentCount: number;
  incompleteDocumentCount?: number;
  isTruncated?: boolean;
  resultTotalCount?: number;
  onConvertChunk: (
    detectionIds: Id<"documentIntelligenceDetections">[]
  ) => Promise<ConvertTagPointsChunkResult>;
  onComplete: (result: {
    createdItemIds: Id<"fieldPilotItems">[];
    skippedExistingCount: number;
    skippedInactiveCount: number;
  }) => void;
  /** Called after each successful chunk so partial failures still enter undo history. */
  onChunkCreated?: (createdItemIds: Id<"fieldPilotItems">[]) => void;
}

export function ConvertTagPointsDialog({
  open,
  onOpenChange,
  tagLabel,
  defaultStatusName,
  unconvertedLocations,
  documentCount,
  incompleteDocumentCount = 0,
  isTruncated = false,
  resultTotalCount,
  onConvertChunk,
  onComplete,
  onChunkCreated,
}: ConvertTagPointsDialogProps) {
  const [isConverting, setIsConverting] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);

  const remainingCount = unconvertedLocations.length;
  const drawingLabel = documentCount === 1 ? "drawing" : "drawings";
  const convertBlocked = isTruncated;

  const handleConfirm = async () => {
    if (remainingCount === 0 || isConverting || convertBlocked) return;

    setIsConverting(true);
    const createdItemIds: Id<"fieldPilotItems">[] = [];
    let skippedExistingCount = 0;
    let skippedInactiveCount = 0;
    const detectionIds = unconvertedLocations.map((location) => location.detectionId);
    const totalChunks = Math.max(1, Math.ceil(detectionIds.length / CONVERSION_CHUNK_SIZE));

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
        const chunk = detectionIds.slice(
          chunkIndex * CONVERSION_CHUNK_SIZE,
          (chunkIndex + 1) * CONVERSION_CHUNK_SIZE
        );
        setProgressLabel(`Creating points… ${chunkIndex + 1}/${totalChunks}`);
        const result = await onConvertChunk(chunk);
        createdItemIds.push(...result.createdItemIds);
        skippedExistingCount += result.skippedExistingItemIds.length;
        skippedInactiveCount += result.skippedInactiveDetectionCount;
        if (result.createdItemIds.length > 0) {
          onChunkCreated?.(result.createdItemIds);
        }
      }

      onComplete({
        createdItemIds,
        skippedExistingCount,
        skippedInactiveCount,
      });
      onOpenChange(false);
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to create points");
    } finally {
      setIsConverting(false);
      setProgressLabel(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isConverting) {
          onOpenChange(nextOpen);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create point measurements</DialogTitle>
          <DialogDescription>
            Create {remainingCount} point measurement{remainingCount === 1 ? "" : "s"} for{" "}
            {tagLabel} across {documentCount} {drawingLabel}? New points will start as{" "}
            {defaultStatusName} and will use Measurement count progress.
          </DialogDescription>
        </DialogHeader>
        {isTruncated ? (
          <p className="text-sm text-status-pending" aria-live="polite">
            Location results are truncated
            {typeof resultTotalCount === "number"
              ? ` (showing loaded detections of ${resultTotalCount} total)`
              : ""}
            . Conversion is blocked until the full detection set can be loaded so
            completeness is not misreported.
          </p>
        ) : null}
        {incompleteDocumentCount > 0 ? (
          <p className="text-sm text-status-pending" aria-live="polite">
            {incompleteDocumentCount} drawing{incompleteDocumentCount === 1 ? "" : "s"}{" "}
            {incompleteDocumentCount === 1 ? "has" : "have"} incomplete indexing. Only
            detections from completed indexes will be converted.
          </p>
        ) : null}
        {progressLabel ? (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {progressLabel}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isConverting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isConverting || remainingCount === 0 || convertBlocked}
            onClick={() => void handleConfirm()}
          >
            {isConverting ? "Creating…" : "Create points"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
